import {Component, Input, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {DataFormatService} from "../../../services/data-format.service";
import {MatSort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import {FormControl} from "@angular/forms";
import {update_closing_feasibility_notes, update_rate_lock, update_financing_term_loans, delete_loan, lender_by_name} from "./graphql/alterLoans";
import {MatSnackBar, MatSnackBarConfig} from "@angular/material/snack-bar";
import {Apollo} from "apollo-angular";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {ConfirmationModalComponent} from "../../../components/confirmation-modal/confirmation-modal.component";
import {formatCurrency} from "@angular/common";
import {debounceTime, distinctUntilChanged, Subject, Subscription} from "rxjs";

const UPDATE_CLOSING_FEASIBILITY_NOTES = update_closing_feasibility_notes;
const UPDATE_RATE_LOCK = update_rate_lock;
const UPDATE_FINANCING_TERM_LOANS = update_financing_term_loans;
const DELETE_LOAN = delete_loan;
const LENDER_BY_NAME = lender_by_name

export interface LoansTable {
  financingtermid: string;
  loanid: string;
  lenderid: string;
  newlenderid: string;
  lender: string;
  newlender: string;
  loantype: string;
  loantypeid: string
  newloantypeid: string;
  amount: number;
  amountFormatted: number;
  newamount: number;
  available: string;
  newavailable: boolean;
}

@Component({
    selector: 'deal-loans',
    templateUrl: './loans.component.html',
    styleUrls: ['./loans.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoansComponent implements OnInit{
  @Input() loansFromModels: any;
  @Input() loansFromProject: any;
  @Input() closingNotes: string;
  @Input() rateLock: boolean;
  @Input() permissions: any;
  @Input() projectID: string;
  @Input() projectDetailsID: string;
  @Input() loanTypes: any;
  @Input() financingTermType: any;
  @ViewChild('dataSort') public dataSort: MatSort;

  loanDataSource: MatTableDataSource<LoansTable>

  loans: any;
  ratelockString: string
  loanColumns: string[] = ['lender', 'loantype', 'amount', 'available'];

  notesEditMode: boolean = false;
  notes: FormControl;
  notesChanged: boolean = false;

  isRateEdit = false;
  isRateLocked: boolean | null = null;

  loansEditMode: boolean = false;

  lenderUpdate = new Subject<string>();
  loading = false;

  constructor(private dataFormat: DataFormatService,
              private apollo: Apollo,
              private _snackBar: MatSnackBar,
              private dialog: MatDialog) {
    this.lenderUpdate.pipe(
      debounceTime(500),
      distinctUntilChanged())
      .subscribe(value => {
        if(value !== ''){
          this.loading = true;
          this.apollo
            .query({
              query: LENDER_BY_NAME,
              variables: {
                name: value
              },
              fetchPolicy: 'no-cache',
            })
            .subscribe(
              ({ data }) => {
                this.loading = false;

                // @ts-ignore
                if(data.lenderByName){
                  // @ts-ignore
                  this.setLenderID(data.lenderByName.id, value);
                } else {
                  this.setLenderID('', value);
                }
              }
            );
        }
      });
  }

  setLenderID(id: string, value: string){
    if(this.loans && this.loans.length > 0){
      for(let i = 0; i < this.loans.length; i++){
        if(this.loans[i].newlender === value.toString()){
          this.loans[i].newlenderid = id;
        }
      }
    }
  }

  ngOnInit() {
    this.isRateLocked = this.rateLock;
    this.ratelockString = this.dataFormat.yesNoFormat(this.rateLock);
    if(this.loansFromModels.length > 0){
      //Get model for this deal
      let latestModel = [];

      let active = [];
      let closed = [];

      for(let i = 0; i < this.loansFromModels.length; i++){
        if(this.loansFromModels[i]['active_model'] === true){
          active = this.loansFromModels[i];
        }

        if(this.loansFromModels[i]['milestone'] && this.loansFromModels[i]['milestone']['name'] === 'Closed'){
          closed = this.loansFromModels[i];
        }
      }

      if(active){
        latestModel = active;
      } else if (closed) {
        latestModel = closed;
      }

      if(latestModel && latestModel.kpiset && latestModel.kpiset.financingTerms){
        this.loans = this.formatLoans(latestModel.kpiset.financingTerms);
      } else {
        this.loans = [];
      }
    } else if (this.loansFromProject.length > 0){
      this.loans = this.formatLoans(this.loansFromProject);
    } else {
      this.loans = [];
    }

    this.loanDataSource = new MatTableDataSource<LoansTable>(this.loans);
    this.notes = new FormControl(this.closingNotes);
  }

  ngAfterViewInit(){
    this.loanDataSource.sort = this.dataSort;
  }

  formatLoans(loans: any){
    let eachLoan = [];
    for(let i = 0; i < loans.length; i++) {
      if(loans[i]['loan']){
        eachLoan.push({
          financingtermid: loans[i]['id'],
          loanid: loans[i]['loan']['id'],
          lenderid: loans[i]['loan']['lender']['id'],
          newlenderid: loans[i]['loan']['lender']['id'],
          lender: loans[i]['loan']['lender']['name'],
          newlender: loans[i]['loan']['lender']['name'],
          loantypeid: loans[i]['loan']['loanType']['id'],
          newloantypeid: loans[i]['loan']['loanType']['id'],
          loantype: loans[i]['loan']['loanType']['name'],
          amount: loans[i]['loan']['amount'],
          amountFormatted: loans[i]['loan']['amount'] ? formatCurrency(loans[i]['loan']['amount'], 'EN', '$', 'USD', '1.0-0') : '$0',
          newamount: loans[i]['loan']['amount'],
          available: this.dataFormat.yesNoFormat(loans[i]['loan']['available_to_investor']),
          newavailable: loans[i]['loan']['available_to_investor']
        });
      }
    }
    eachLoan.sort(this.loanSort(eachLoan));
    return eachLoan;
  }

  //This is to sort the loans by type asc, amount desc
  loanSort(loans: any): any {
    function compare( a:any, b:any ) {
      if(a.loantype === b.loantype){
        if ( a.amount > b.amount){
          return -1;
        } else if ( a.amount < b.amount ){
          return 1;
        } else {
          return 0;
        }
      } else if (a.loantype < b.loantype){
        return -1;
      }
      return 1;
    }
    loans.sort( compare );
  }

  toggleNotesEditMode(){
    if(this.notesEditMode && this.notesChanged){
      this.apollo
        .mutate({
          mutation: UPDATE_CLOSING_FEASIBILITY_NOTES,
          variables: {
            id: this.projectDetailsID,
            notes: this.notes.value
          },
        })
        .subscribe(
          ({ data }) => {
            // @ts-ignore
            this.closingNotes = data.updateClosingFeasibilityNotes.closing_feasibility_notes;
            this.notes.setValue(this.closingNotes);
            this.notesChanged = false;
            this.openSnackBar('Saved Loan Data successfully.');
          }
        );
    }
    this.notesEditMode = !this.notesEditMode;
  }

  toggleRateEditMode(){
    let newRateLockString = this.dataFormat.yesNoFormat(this.isRateLocked);
    if(this.isRateEdit && this.ratelockString !== newRateLockString){
      this.apollo
        .mutate({
          mutation: UPDATE_RATE_LOCK,
          variables: {
            id: this.projectDetailsID,
            rate_locked_flag: this.isRateLocked
          },
        })
        .subscribe(
          ({ data }) => {
            // @ts-ignore
            this.isRateLocked = data.updateRateLock.rate_locked_flag;
            // @ts-ignore
            this.ratelockString = this.dataFormat.yesNoFormat(data.updateRateLock.rate_locked_flag);

            this.openSnackBar('Saved Loan Data successfully.');
          }
        );
    }
    this.isRateEdit = !this.isRateEdit;
  }

  toggleLoansEditMode(){
    if(this.loansEditMode){
      let upsertLoans = [];
      for(let i = 0; i < this.loans.length; i++){
        if(this.loans[i].lender !== this.loans[i].newlender
        || this.loans[i].loantypeid !== this.loans[i].newloantypeid
        || this.loans[i].amount !== this.loans[i].newamount
        || this.loans[i].available !== this.dataFormat.yesNoFormat(this.loans[i].newavailable)){
          if(this.loans[i].financingtermid !== '' && this.loans[i].loanid !== ''){
            //If existing loan with existing lender
            if(this.loans[i].newlenderid !== ''){
              upsertLoans.push({
                id: this.loans[i].financingtermid,
                loan: {
                  upsert: {
                    id: this.loans[i].loanid,
                    loan_type_id: this.loans[i].newloantypeid,
                    amount: this.loans[i].newamount,
                    available_to_investor: this.loans[i].newavailable,
                    lender_id: this.loans[i].newlenderid
                  }
                }
              });
            }
            //If existing loan with new lender
            else {
              upsertLoans.push({
                id: this.loans[i].financingtermid,
                loan: {
                  upsert: {
                    id: this.loans[i].loanid,
                    loan_type_id: this.loans[i].newloantypeid,
                    amount: this.loans[i].newamount,
                    available_to_investor: this.loans[i].newavailable,
                    lender: {
                      create: {
                        name: this.loans[i].newlender
                      }
                    }
                  }
                }
              });
            }
          } else {
            //if new loan with existing lender
            if(this.loans[i].newlenderid !== ''){
              upsertLoans.push({
                financing_term_type_id: this.financingTermType.id,
                loan: {
                  upsert: {
                    loan_type_id: this.loans[i].newloantypeid,
                    amount: this.loans[i].newamount,
                    available_to_investor: this.loans[i].newavailable,
                    lender_id: this.loans[i].newlenderid
                  }
                }
              });
            } else {
              //if new loan with new lender
              upsertLoans.push({
                financing_term_type_id: this.financingTermType.id,
                loan: {
                  upsert: {
                    loan_type_id: this.loans[i].newloantypeid,
                    amount: this.loans[i].newamount,
                    available_to_investor: this.loans[i].newavailable,
                    lender: {
                      create: {
                        name: this.loans[i].newlender
                      }
                    }
                  }
                }
              });
            }
          }
        }
      }

      if(upsertLoans.length > 0){
        this.apollo
          .mutate({
            mutation: UPDATE_FINANCING_TERM_LOANS,
            variables: {
              input: {
                id: this.projectID,
                financingTerms: {
                  upsert: upsertLoans
                }
              }
            },
          })
          .subscribe(
            ({ data }) => {
              // @ts-ignore
              this.loansFromProject = data.upsertFinancingTermLoans.financingTerms;
              this.loans = this.formatLoans(this.loansFromProject);
              this.loanDataSource = new MatTableDataSource<LoansTable>(this.loans);
              this.openSnackBar('Saved Loan Data successfully.');
            }
          );
      }
    }
    this.loansEditMode = !this.loansEditMode;
  }

  addLoan(){
    if(this.loansEditMode){
      this.loans.push({
        loanid: '',
        lenderid: '',
        newlenderid: '',
        lender: '',
        newlender: '',
        loantypeid: '',
        newloantypeid: '',
        loantype: '',
        amount: 0,
        amountFormatted: '$0',
        newamount: 0,
        available: false,
        newavailable: false
      });
      this.loanDataSource = new MatTableDataSource<LoansTable>(this.loans);
    }
  }

  openSnackBar(message: string) {
    const config = new MatSnackBarConfig();
    config.panelClass = ['save-success'];
    config.horizontalPosition = "center";
    config.verticalPosition = "top";
    config.duration = 3000;
    this._snackBar.open(message, 'Ok', config);
  }

  deleteLoan(row: any, i: number){
    if(row.hasOwnProperty('financingtermid') && row.financingtermid !== ''){
      const ref: MatDialogRef<ConfirmationModalComponent> = this.dialog.open(
        ConfirmationModalComponent,
        {
          width: '600px',
          height: '380px',
          data: {
            modalTitle: 'Delete Loan',
            actionMessage: 'Are you sure you want to delete the Loan?',
            modalInformation: [
              {
                title: 'Lender',
                info: row.lender
              },
              {
                title: 'Loan Type',
                info: row.loantype
              },
              {
                title: 'Amount',
                info: row.amount
              },
              {
                title: 'Availiable to Investor',
                info: this.dataFormat.yesNoFormat(row.available)
              },
            ]
          },
          backdropClass: 'confirmDialogComponent',
          hasBackdrop: true,
        });
      ref.afterClosed().subscribe((data) => {
        if(data.clicked === 'Ok'){
          this.apollo
            .mutate({
              mutation: DELETE_LOAN,
              variables: {
                id: row.financingtermid
              },
            })
            .subscribe(
              ({ data }) => {
                this.loans.splice(i, 1);
                this.loanDataSource = new MatTableDataSource<LoansTable>(this.loans);
                this.loanDataSource.sort = this.dataSort;
                this.openSnackBar('Deleted Loan.');
              }
            );
        }
      });
    } else {
      this.loans.splice(i, 1);
      this.loanDataSource = new MatTableDataSource<LoansTable>(this.loans);
      this.loanDataSource.sort = this.dataSort;
    }
  }

  checkLoans(): boolean{
    let errors = false;
    if(this.loansEditMode && this.loans && this.loans.length > 0){
      for(let i = 0; i < this.loans.length; i++){
        if(this.loans[i].newlender === '' || this.loans[i].newloantypeid === '' || this.loans[i].newamount === '' || this.loans[i].newavailable === null){
          errors = true;
          break;
        }
      }
    }
    return errors;
  }
}
